// TennisTrack Hub — Profilo Atleta | 029 Project
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import './Atleti.css';

const ProfiloAtleta = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [atleta, setAtleta] = useState(null);
  const [lezioni, setLezioni] = useState([]);
  const [pacchetti, setPacchetti] = useState([]);
  const [allenamenti, setAllenamenti] = useState([]);
  const [tabAttiva, setTabAttiva] = useState('info');
  const [loading, setLoading] = useState(true);

  // Modal nuova lezione
  const [showModalLezione, setShowModalLezione] = useState(false);
  const [showModalPacchetto, setShowModalPacchetto] = useState(false);

  const caricaDati = useCallback(async () => {
    try {
      const [resAtleta, resLezioni, resPacchetti, resAllenamenti] = await Promise.all([
        api.get(`/atleti/${id}`),
        api.get(`/lezioni/atleta/${id}`),
        api.get(`/lezioni/pacchetti/atleta/${id}`),
        api.get(`/allenamenti/atleta/${id}`)
      ]);
      setAtleta(resAtleta.data.atleta);
      setLezioni(resLezioni.data.lezioni);
      setPacchetti(resPacchetti.data.pacchetti);
      setAllenamenti(resAllenamenti.data.allenamenti);
    } catch {
      navigate('/atleti');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { caricaDati(); }, [caricaDati]);

  const segnaLezioneSvolta = async (lezioneId) => {
    try {
      await api.patch(`/lezioni/${lezioneId}/svolta`);
      caricaDati();
    } catch (err) {
      console.error(err);
    }
  };

  const aggiornaPagamento = async (pacchettoId, pagato) => {
    try {
      await api.patch(`/lezioni/pacchetti/${pacchettoId}/pagamento`, {
        pagato,
        dataPagamento: pagato ? new Date().toISOString().split('T')[0] : null
      });
      caricaDati();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <div className="page-layout"><Navbar /><div className="loading-msg">Caricamento...</div></div>;
  if (!atleta) return null;

  const sportIcon = { tennis: '🎾', fitness: '💪', altro: '🏅' }[atleta.sport] || '🏅';
  const lezioniSvolte = lezioni.filter(l => l.svolta).length;
  const pacchettoAttivo = pacchetti[0];

  return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">

        {/* Header atleta */}
        <div className="profilo-header card">
          <div className="profilo-avatar">{atleta.nome[0]}{atleta.cognome[0]}</div>
          <div className="profilo-info">
            <h1>{atleta.nome} {atleta.cognome}</h1>
            <div className="profilo-meta">
              <span>{sportIcon} {atleta.sport}</span>
              {atleta.eta && <span>· {atleta.eta} anni</span>}
              <span className={`livello-badge livello-${atleta.livello === 'principiante' ? 'verde' : atleta.livello === 'intermedio' ? 'giallo' : 'rosso'}`}>
                {atleta.livello}
              </span>
            </div>
          </div>
          <div className="profilo-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/atleti/${id}/modifica`)}>
              ✏️ Modifica
            </button>
          </div>
        </div>

        {/* Riepilogo rapido */}
        <div className="profilo-stats">
          <div className="card stat-card">
            <span className="stat-number">{lezioni.length}</span>
            <span className="stat-label">Lezioni totali</span>
          </div>
          <div className="card stat-card">
            <span className="stat-number">{lezioniSvolte}</span>
            <span className="stat-label">Svolte</span>
          </div>
          <div className="card stat-card">
            <span className="stat-number">{pacchettoAttivo ? (pacchettoAttivo.totale_lezioni - pacchettoAttivo.lezioni_svolte) : '—'}</span>
            <span className="stat-label">Lezioni rimanenti</span>
          </div>
          <div className="card stat-card">
            <span className={`stat-number ${pacchettoAttivo && !pacchettoAttivo.pagato ? 'text-error' : ''}`}>
              {pacchettoAttivo ? (pacchettoAttivo.pagato ? '✅' : '⚠️') : '—'}
            </span>
            <span className="stat-label">Pagamento</span>
          </div>
        </div>

        {/* Tab navigazione */}
        <div className="tab-bar">
          {['info', 'lezioni', 'allenamenti', 'pacchetti'].map(tab => (
            <button
              key={tab}
              className={`tab-btn ${tabAttiva === tab ? 'active' : ''}`}
              onClick={() => setTabAttiva(tab)}
            >
              {{ info: '👤 Info', lezioni: '📅 Lezioni', allenamenti: '💪 Allenamenti', pacchetti: '💳 Pacchetti' }[tab]}
            </button>
          ))}
        </div>

        {/* Tab: Info */}
        {tabAttiva === 'info' && (
          <div className="card tab-content">
            <div className="info-grid">
              {atleta.altezza_cm && <div className="info-item"><span className="info-label">Altezza</span><span>{atleta.altezza_cm} cm</span></div>}
              {atleta.peso_kg && <div className="info-item"><span className="info-label">Peso</span><span>{atleta.peso_kg} kg</span></div>}
              {atleta.telefono && <div className="info-item"><span className="info-label">Telefono</span><span>{atleta.telefono}</span></div>}
              {atleta.email && <div className="info-item"><span className="info-label">Email</span><span>{atleta.email}</span></div>}
              <div className="info-item"><span className="info-label">Iscritto dal</span><span>{new Date(atleta.data_iscrizione).toLocaleDateString('it-IT')}</span></div>
            </div>
            {atleta.note && (
              <div className="info-note">
                <span className="info-label">Note</span>
                <p>{atleta.note}</p>
              </div>
            )}
          </div>
        )}

        {/* Tab: Lezioni */}
        {tabAttiva === 'lezioni' && (
          <div className="tab-content">
            <div className="tab-actions">
              <button className="btn btn-primary btn-sm" onClick={() => setShowModalLezione(true)}>
                + Nuova lezione
              </button>
            </div>
            {lezioni.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📅</div>
                <h3>Nessuna lezione</h3>
                <p>Aggiungi la prima lezione per questo atleta.</p>
              </div>
            ) : (
              <div className="lezioni-list">
                {lezioni.map(l => (
                  <div key={l.id} className={`card lezione-item ${l.svolta ? 'svolta' : ''}`}>
                    <div className="lezione-data">
                      <strong>{new Date(l.data_ora).toLocaleDateString('it-IT')}</strong>
                      <span>{new Date(l.data_ora).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="lezione-info">
                      <span>{l.tipo === 'tennis' ? '🎾' : '💪'} {l.tipo}</span>
                      <span>{l.durata_min} min</span>
                      {l.note && <span className="lezione-note">{l.note}</span>}
                    </div>
                    <div className="lezione-stato">
                      {l.svolta ? (
                        <span className="badge-svolta">✅ Svolta</span>
                      ) : (
                        <button className="btn btn-secondary btn-xs" onClick={() => segnaLezioneSvolta(l.id)}>
                          Segna svolta
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab: Allenamenti */}
        {tabAttiva === 'allenamenti' && (
          <div className="tab-content">
            <div className="tab-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/allenamenti/nuovo/${id}?tipo=tennis`)}>
                🎾 Tennis
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => navigate(`/allenamenti/nuovo/${id}?tipo=fitness`)}>
                💪 Fitness
              </button>
            </div>
            {allenamenti.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">💪</div>
                <h3>Nessun allenamento</h3>
                <p>Aggiungi il primo allenamento per questo atleta.</p>
              </div>
            ) : (
              <div className="allenamenti-list">
                {allenamenti.map(a => (
                  <Link to={`/allenamenti/${a.id}`} key={a.id} className="card allenamento-item">
                    <div className="all-data">
                      <strong>{new Date(a.data).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>
                    </div>
                    <div className="all-info">
                      <span>{a.tipo === 'tennis' ? '🎾' : '💪'} {a.tipo}</span>
                      {a.valutazione > 0 && (
                        <span className="all-stelle">{'★'.repeat(a.valutazione)}{'☆'.repeat(5 - a.valutazione)}</span>
                      )}
                      <div className="all-counts">
                        {parseInt(a.n_esercizi_tennis) + parseInt(a.n_esercizi_fitness) > 0 && (
                          <span>{parseInt(a.n_esercizi_tennis) + parseInt(a.n_esercizi_fitness)} esercizi</span>
                        )}
                        {parseInt(a.n_video) > 0 && <span>📹 {a.n_video}</span>}
                      </div>
                    </div>
                    <div className="atleta-arrow">›</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab: Pacchetti */}
        {tabAttiva === 'pacchetti' && (
          <div className="tab-content">
            <div className="tab-actions">
              <button className="btn btn-primary btn-sm" onClick={() => setShowModalPacchetto(true)}>
                + Nuovo pacchetto
              </button>
            </div>
            {pacchetti.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">💳</div>
                <h3>Nessun pacchetto</h3>
                <p>Crea un pacchetto per tracciare le lezioni e i pagamenti.</p>
              </div>
            ) : (
              <div className="pacchetti-list">
                {pacchetti.map(p => (
                  <div key={p.id} className="card pacchetto-item">
                    <div className="pacchetto-info">
                      <h3>{p.nome}</h3>
                      <div className="pacchetto-meta">
                        <span>📋 {p.lezioni_svolte}/{p.totale_lezioni} lezioni</span>
                        {p.prezzo && <span>💶 €{p.prezzo}</span>}
                      </div>
                      {/* Barra avanzamento */}
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${(p.lezioni_svolte / p.totale_lezioni) * 100}%` }}
                        />
                      </div>
                    </div>
                    <div className="pacchetto-pagamento">
                      {p.pagato ? (
                        <span className="badge-pagato">✅ Pagato</span>
                      ) : (
                        <button
                          className="btn btn-primary btn-xs"
                          onClick={() => aggiornaPagamento(p.id, true)}
                        >
                          Segna pagato
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Modal Nuova Lezione */}
        {showModalLezione && (
          <ModalNuovaLezione
            atletaId={id}
            onClose={() => setShowModalLezione(false)}
            onSalva={() => { setShowModalLezione(false); caricaDati(); }}
          />
        )}

        {/* Modal Nuovo Pacchetto */}
        {showModalPacchetto && (
          <ModalNuovoPacchetto
            atletaId={id}
            onClose={() => setShowModalPacchetto(false)}
            onSalva={() => { setShowModalPacchetto(false); caricaDati(); }}
          />
        )}

      </main>
    </div>
  );
};

// ============================================================
// MODAL NUOVA LEZIONE
// ============================================================
const ModalNuovaLezione = ({ atletaId, onClose, onSalva }) => {
  const [form, setForm] = useState({ dataOra: '', durataMin: 60, tipo: 'tennis', note: '' });
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/lezioni', { atletaId: parseInt(atletaId), ...form });
      onSalva();
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore nel salvataggio.');
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box card" onClick={e => e.stopPropagation()}>
        <h3>Nuova lezione</h3>
        {errore && <div className="alert alert-error">{errore}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Data e ora *</label>
            <input type="datetime-local" value={form.dataOra} onChange={e => setForm({...form, dataOra: e.target.value})} required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Durata (min)</label>
              <input type="number" value={form.durataMin} onChange={e => setForm({...form, durataMin: e.target.value})} min="15" max="240" />
            </div>
            <div className="form-group">
              <label>Tipo</label>
              <select value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})}>
                <option value="tennis">🎾 Tennis</option>
                <option value="fitness">💪 Fitness</option>
                <option value="altro">🏅 Altro</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Note</label>
            <textarea value={form.note} onChange={e => setForm({...form, note: e.target.value})} rows={2} placeholder="Note opzionali..." />
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Salvo...' : 'Crea lezione'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================
// MODAL NUOVO PACCHETTO
// ============================================================
const ModalNuovoPacchetto = ({ atletaId, onClose, onSalva }) => {
  const [form, setForm] = useState({ nome: 'Pacchetto lezioni', totaleLezioni: 10, prezzo: '', pagato: false });
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/lezioni/pacchetti', { atletaId: parseInt(atletaId), ...form });
      onSalva();
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore nel salvataggio.');
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box card" onClick={e => e.stopPropagation()}>
        <h3>Nuovo pacchetto</h3>
        {errore && <div className="alert alert-error">{errore}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nome pacchetto</label>
            <input type="text" value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>N° lezioni</label>
              <input type="number" value={form.totaleLezioni} onChange={e => setForm({...form, totaleLezioni: e.target.value})} min="1" />
            </div>
            <div className="form-group">
              <label>Prezzo (€)</label>
              <input type="number" value={form.prezzo} onChange={e => setForm({...form, prezzo: e.target.value})} placeholder="es. 200" min="0" step="0.01" />
            </div>
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input type="checkbox" checked={form.pagato} onChange={e => setForm({...form, pagato: e.target.checked})} />
              Già pagato
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Salvo...' : 'Crea pacchetto'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfiloAtleta;
